import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, MapPin, Calendar, Clock, Users, MessageSquare, Send, UserPlus, UserMinus,
  Mountain, Dumbbell, Car, Landmark, Wine, UtensilsCrossed, Laptop, Languages, Sparkles,
  Trash2, AlertTriangle, Navigation, Heart, Bookmark, Reply, ChevronDown, ChevronUp,
  Gauge, Wallet, Globe, ClipboardList
} from 'lucide-react';
import { Activity, ActivityCategory, ACTIVITY_CATEGORIES, ActivityComment, ActivityCommentReply } from '../../types/activities';
import { CATEGORY_IMAGES, CATEGORY_GRADIENTS, CATEGORY_ACCENT } from '../../constants/activity-categories';
import { Timestamp } from 'firebase/firestore';

interface ActivityDetailModalProps {
  activity: Activity;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
  currentUsername?: string;
  currentUserAvatar?: string;
  onJoin: (activityId: string) => void;
  onLeave: (activityId: string) => void;
  onComment: (activityId: string, content: string) => void;
  onReply?: (activityId: string, commentId: string, content: string) => void;
  onLike?: (activityId: string) => void;
  onFavorite?: (activityId: string) => void;
  onDelete?: (activityId: string) => void;
  isJoining?: boolean;
  isAdmin?: boolean;
  isLiked?: boolean;
  isFavorited?: boolean;
}

const CATEGORY_ICONS: Record<ActivityCategory, React.ReactNode> = {
  'hiking': <Mountain className="w-5 h-5" />,
  'sport': <Dumbbell className="w-5 h-5" />,
  'roadtrip': <Car className="w-5 h-5" />,
  'city-tour': <MapPin className="w-5 h-5" />,
  'cultural': <Landmark className="w-5 h-5" />,
  'nightlife': <Wine className="w-5 h-5" />,
  'food': <UtensilsCrossed className="w-5 h-5" />,
  'coworking': <Laptop className="w-5 h-5" />,
  'language-exchange': <Languages className="w-5 h-5" />,
  'other': <Sparkles className="w-5 h-5" />,
};

function formatFullDate(timestamp: { toDate?: () => Date } | Date): string {
  const date = 'toDate' in timestamp && timestamp.toDate ? timestamp.toDate() : timestamp as Date;
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCommentDate(timestamp: Timestamp | { toDate?: () => Date }): string {
  const date = 'toDate' in timestamp && timestamp.toDate ? timestamp.toDate() : new Date();
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "A l'instant";
  if (minutes < 60) return `il y a ${minutes}min`;
  if (hours < 24) return `il y a ${hours}h`;
  if (days < 7) return `il y a ${days}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function CommentItem({
  comment,
  activityUserId,
  currentUserId,
  onReplyClick,
}: {
  comment: ActivityComment;
  activityUserId: string;
  currentUserId?: string;
  onReplyClick: (commentId: string) => void;
}) {
  const [showReplies, setShowReplies] = React.useState(false);
  const replies = comment.replies || [];

  return (
    <div className="group">
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0 shadow-sm">
          {comment.username.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="bg-gray-50 dark:bg-gray-700/40 rounded-2xl rounded-tl-sm px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{comment.username}</span>
              {comment.userId === activityUserId && (
                <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-full font-medium">Organisateur</span>
              )}
              <span className="text-[11px] text-gray-400">{formatCommentDate(comment.createdAt)}</span>
            </div>
            <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">{comment.content}</p>
          </div>
          <div className="flex items-center gap-3 mt-1 ml-2">
            {currentUserId && (
              <button
                onClick={() => onReplyClick(comment.id)}
                className="flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-teal-500 transition-colors"
              >
                <Reply className="w-3 h-3" />
                Répondre
              </button>
            )}
            {replies.length > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="flex items-center gap-1 text-[11px] font-medium text-teal-500 hover:text-teal-600 transition-colors"
              >
                {showReplies ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {replies.length} réponse{replies.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
          <AnimatePresence>
            {showReplies && replies.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 ml-2 space-y-2 border-l-2 border-teal-200 dark:border-teal-800/50 pl-3">
                  {replies.map((r: ActivityCommentReply) => (
                    <div key={r.id} className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                        {r.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200">{r.username}</span>
                          <span className="text-[10px] text-gray-400">{formatCommentDate(r.createdAt)}</span>
                        </div>
                        <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-relaxed">{r.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export function ActivityDetailModal({
  activity,
  isOpen,
  onClose,
  currentUserId,
  onJoin,
  onLeave,
  onComment,
  onReply,
  onLike,
  onFavorite,
  onDelete,
  isJoining,
  isAdmin,
  isLiked,
  isFavorited,
}: ActivityDetailModalProps) {
  const [comment, setComment] = React.useState('');
  const [replyingTo, setReplyingTo] = React.useState<string | null>(null);
  const [replyText, setReplyText] = React.useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const commentsEndRef = React.useRef<HTMLDivElement>(null);
  const replyInputRef = React.useRef<HTMLInputElement>(null);

  const isParticipant = currentUserId ? activity.participants.some(p => p.userId === currentUserId) : false;
  const isOwner = currentUserId === activity.userId;
  const spotsLeft = activity.maxParticipants - activity.participants.length;
  const isFull = spotsLeft <= 0;
  const catInfo = ACTIVITY_CATEGORIES.find(c => c.id === activity.category);
  const accent = CATEGORY_ACCENT[activity.category];
  const likesCount = activity.likes?.length || 0;

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  React.useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    onComment(activity.id, comment.trim());
    setComment('');
    setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !replyingTo) return;
    onReply?.(activity.id, replyingTo, replyText.trim());
    setReplyText('');
    setReplyingTo(null);
  };

  const handleDelete = () => {
    onDelete?.(activity.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden flex-shrink-0" />

        <div className="relative h-48 sm:h-56 flex-shrink-0 overflow-hidden">
          <img
            src={CATEGORY_IMAGES[activity.category]}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 bg-gradient-to-t ${CATEGORY_GRADIENTS[activity.category]} opacity-50`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/90 dark:bg-gray-900/80 text-gray-800 dark:text-gray-200 backdrop-blur-sm shadow-sm">
              {CATEGORY_ICONS[activity.category]}
              {catInfo?.label}
            </span>
            <div className="flex items-center gap-2">
              {(isOwner || isAdmin) && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 bg-red-500/80 hover:bg-red-500 text-white rounded-full backdrop-blur-sm transition-colors touch-manipulation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 bg-black/30 hover:bg-black/50 text-white rounded-full backdrop-blur-sm transition-colors touch-manipulation"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 leading-tight drop-shadow-md">
              {activity.title}
            </h2>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full ${accent.bg} flex items-center justify-center text-white text-xs font-bold ring-2 ring-white/30`}>
                {activity.username.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white text-sm font-medium drop-shadow-sm">{activity.username}</p>
                <p className="text-white/70 text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {activity.city}, {activity.country}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700/50 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onLike?.(activity.id)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              <span>{likesCount}</span>
            </button>
            <button
              onClick={() => onFavorite?.(activity.id)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                isFavorited ? 'text-amber-500' : 'text-gray-500 hover:text-amber-500'
              }`}
            >
              <Bookmark className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
              <span>{isFavorited ? 'Sauvegarde' : 'Sauvegarder'}</span>
            </button>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <MessageSquare className="w-4 h-4" />
            <span>{activity.comments.length}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 text-center">
                <Calendar className="w-5 h-5 text-teal-500 mx-auto mb-1.5" />
                <p className="text-xs text-gray-400 mb-0.5">Date</p>
                <p className="text-[13px] font-semibold text-gray-900 dark:text-white leading-tight">
                  {formatFullDate(activity.date)}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 text-center">
                <Clock className="w-5 h-5 text-teal-500 mx-auto mb-1.5" />
                <p className="text-xs text-gray-400 mb-0.5">Heure</p>
                <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
                  {activity.time}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 text-center">
                <Users className="w-5 h-5 text-teal-500 mx-auto mb-1.5" />
                <p className="text-xs text-gray-400 mb-0.5">Places</p>
                <p className={`text-[13px] font-semibold ${isFull ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                  {activity.participants.length}/{activity.maxParticipants}
                </p>
              </div>
              {activity.meetingPoint ? (
                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 text-center">
                  <Navigation className="w-5 h-5 text-teal-500 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-400 mb-0.5">RDV</p>
                  <p className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {activity.meetingPoint}
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 text-center">
                  <MapPin className="w-5 h-5 text-teal-500 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-400 mb-0.5">Lieu</p>
                  <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
                    {activity.city}
                  </p>
                </div>
              )}
            </div>

            {!isFull && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(activity.participants.length / activity.maxParticipants) * 100}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                      className={`h-full rounded-full ${
                        spotsLeft <= 3 ? 'bg-amber-500' : 'bg-teal-500'
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium ${spotsLeft <= 3 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {spotsLeft} place{spotsLeft > 1 ? 's' : ''} restante{spotsLeft > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}

            {(activity.difficulty || activity.budget || activity.spokenLanguages?.length || activity.requirements) && (
              <div className="mb-6 flex flex-wrap gap-2">
                {activity.difficulty && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/30 text-xs font-medium text-gray-700 dark:text-gray-300">
                    <Gauge className="w-3.5 h-3.5 text-teal-500" />
                    {activity.difficulty === 'easy' ? 'Facile' : activity.difficulty === 'moderate' ? 'Modéré' : 'Sportif'}
                  </span>
                )}
                {activity.budget && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/30 text-xs font-medium text-gray-700 dark:text-gray-300">
                    <Wallet className="w-3.5 h-3.5 text-teal-500" />
                    {activity.budget === 'free' ? 'Gratuit' : activity.budget === 'cheap' ? 'Pas cher' : activity.budget === 'moderate' ? 'Moyen' : 'Cher'}
                  </span>
                )}
                {activity.spokenLanguages && activity.spokenLanguages.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/30 text-xs font-medium text-gray-700 dark:text-gray-300">
                    <Globe className="w-3.5 h-3.5 text-teal-500" />
                    {activity.spokenLanguages.map(l => {
                      const flags: Record<string, string> = { fr: '🇫🇷', en: '🇬🇧', es: '🇪🇸', pt: '🇧🇷', de: '🇩🇪', it: '🇮🇹', ja: '🇯🇵', ko: '🇰🇷', zh: '🇨🇳', other: '🌍' };
                      return flags[l] || l;
                    }).join(' ')}
                  </span>
                )}
                {activity.requirements && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-xs font-medium text-amber-700 dark:text-amber-300">
                    <ClipboardList className="w-3.5 h-3.5" />
                    {activity.requirements}
                  </span>
                )}
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Description</h3>
              <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                {activity.description}
              </p>
            </div>

            {activity.participants.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-teal-500" />
                  Participants ({activity.participants.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {activity.participants.map((p) => (
                    <div
                      key={p.userId}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-100 dark:border-gray-700/50"
                    >
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                        {p.username.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{p.username}</span>
                      {p.userId === activity.userId && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-full font-medium">Organisateur</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-teal-500" />
                Discussion ({activity.comments.length})
              </h3>

              {activity.comments.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/20 rounded-2xl">
                  <MessageSquare className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Aucun commentaire. Lancez la discussion !
                  </p>
                </div>
              ) : (
                <div className="space-y-4 mb-4">
                  {activity.comments.map((c: ActivityComment) => (
                    <CommentItem
                      key={c.id}
                      comment={c}
                      activityUserId={activity.userId}
                      currentUserId={currentUserId}
                      onReplyClick={(commentId) => setReplyingTo(commentId)}
                    />
                  ))}
                  <div ref={commentsEndRef} />
                </div>
              )}

              {replyingTo && currentUserId && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-100 dark:border-teal-800/30"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-teal-600 dark:text-teal-400 flex items-center gap-1">
                      <Reply className="w-3 h-3" />
                      Réponse à {activity.comments.find(c => c.id === replyingTo)?.username}
                    </span>
                    <button
                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                      className="p-1 hover:bg-teal-100 dark:hover:bg-teal-800/30 rounded-full transition-colors"
                    >
                      <X className="w-3.5 h-3.5 text-teal-500" />
                    </button>
                  </div>
                  <form onSubmit={handleSubmitReply} className="flex gap-2">
                    <input
                      ref={replyInputRef}
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Votre réponse..."
                      maxLength={500}
                      className="flex-1 px-3.5 py-2.5 border border-teal-200 dark:border-teal-700 rounded-xl text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                    <button
                      type="submit"
                      disabled={!replyText.trim()}
                      className="p-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl transition-colors touch-manipulation disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                </motion.div>
              )}

              {currentUserId && (
                <form onSubmit={handleSubmitComment} className="flex gap-2">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0 shadow-sm">
                    ?
                  </div>
                  <div className="flex-1 flex gap-2">
                    <input
                      type="text"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Écrire un commentaire..."
                      maxLength={500}
                      className="flex-1 px-3.5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={!comment.trim()}
                      className="p-2.5 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white rounded-xl transition-colors touch-manipulation disabled:opacity-50 shadow-sm"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {currentUserId && !isOwner && (
          <div className="flex-shrink-0 p-4 bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700/50">
            {isParticipant ? (
              <button
                onClick={() => onLeave(activity.id)}
                disabled={isJoining}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors text-sm font-semibold touch-manipulation disabled:opacity-50"
              >
                <UserMinus className="w-4.5 h-4.5" />
                Se désinscrire
              </button>
            ) : (
              <motion.button
                onClick={() => onJoin(activity.id)}
                disabled={isJoining || isFull}
                whileTap={{ scale: 0.97 }}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white rounded-xl transition-colors text-sm font-semibold shadow-lg shadow-teal-600/20 touch-manipulation disabled:opacity-50"
              >
                <UserPlus className="w-4.5 h-4.5" />
                {isFull ? 'Complet' : "S'inscrire à l'activité"}
              </motion.button>
            )}
          </div>
        )}
      </motion.div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm">
            <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden" />
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Supprimer cette activité ?
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                L'activité et tous les commentaires seront supprimés définitivement.
              </p>
              <div className="flex gap-2.5">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors touch-manipulation"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
